import React from 'react'
import { Formik } from 'formik'
import { useCallback, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { SetActivityDetails, UploadPartnerDriversCSV } from 'src/constants/actionTypes'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { InviteWorkersViaCSVFormValues } from './InviteWorkersViaCSVForm.interface'
import { InviteWorkersViaCSVSchema } from './InviteWorkersViaCSVForm.validator'
import { getMarkets } from 'src/selectors/market'
import { showToast } from 'src/utils/toast'
import { getPartnerId } from 'src/selectors/user'
import { getOnboardingTemplates } from 'src/selectors/onboarding'
import { handleApiError } from 'src/utils/errorHandlers'
import { processOnboardingCsvErrors } from 'src/utils/csvErrors'
import { enqueueSnackbar } from 'notistack'
import { activitiesService } from 'src/services/ActivitiesService'
import { BasicModalDescription, BasicModalHeader } from 'src/components/shared/modal'
import { NoteMajor } from '@shopify/polaris-icons'
import { InviteWorkersViaCSVFormContent } from './InviteWorkersViaCSVFormContent'
import tw from 'twin.macro'
import { Button } from '../../../shared/Button'
import { Divider } from '@mui/material'
import { GSIcon } from '../../../shared/GSIcon'

interface Props {
  onSubmit(): void
  onClose(): void
}

export const InviteWorkersViaCSVForm = ({ onSubmit, onClose }: Props) => {
  const dispatch = useDispatch()

  const markets = useSelector(getMarkets)
  const onboardingTemplates = useSelector(getOnboardingTemplates)
  const partnerId = useSelector(getPartnerId)

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [file, setFile] = useState<File | null>(null)

  const handleFormSubmit = async (values: InviteWorkersViaCSVFormValues) => {
    if (!partnerId) {
      return
    }

    setIsLoading(true)

    try {
      await createAsyncAction(
        dispatch,
        UploadPartnerDriversCSV.request({ file, partnerId, ...values }),
      )

      showToast('Contractors invited!', { variant: 'success' })

      onSubmit()
      onClose()
    } catch (err: any) {
      if (Array.isArray(err)) {
        const processedPaymentCsvErrors = processOnboardingCsvErrors(err)

        const activity = activitiesService.addActivity({
          title: 'File upload error',
          text: `Unable to proceed due to ${processedPaymentCsvErrors.length} errors (${file?.name})`,
          content: processedPaymentCsvErrors,
        })

        enqueueSnackbar(activity.title, {
          variant: 'errorWithDetails',
          detailsPayload: activity,
          persist: true,
          onDetailsClick: (activity) => {
            dispatch(SetActivityDetails.success(activity))
          },
        })
      } else {
        handleApiError(err)
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleUploadFile = useCallback(async (file: File) => {
    setFile(file)
  }, [])

  const handleRemoveFile = useCallback(() => {
    setFile(null)
  }, [])

  return (
    <div css={tw`max-w-[528px]`} data-testid="contractors-onboarding-invite-csv-modal">
      <BasicModalHeader
        title="Invite contractor"
        onClose={onClose}
        closeButtonDataTestId="contractors-onboarding-invite-csv-modal-close-btn"
      />
      <div css={tw`flex items-center justify-between gap-4 mb-4`}>
        <BasicModalDescription
          title="Bulk invite via CSV"
          icon={<NoteMajor width={20} height={20} />}
          description="Upload a CSV to bulk invite contractors — invite links will be generated automatically and sent to each contractor. For efficiency, use our CSV template by clicking the download button."
        />
        <a href="/bulk_CSV_example.csv" download="bulk_CSV_example.csv" css={tw`flex-shrink-0`}>
          <Button
            className={tw`mr-4`}
            size="small"
            color="grey"
            rightIcon={<GSIcon iconName="gs_file_download" />}
            data-testid="contractors-onboarding-invite-csv-download-template-btn"
          >
            CSV template
          </Button>
        </a>
      </div>
      <Divider css={tw`mt-4 mx-4 border-[#EDEDED] `} />
      <Formik
        initialValues={{
          partnerMarketId: markets?.length === 1 ? markets[0].id : undefined,
          collectDataTemplateId:
            onboardingTemplates?.length === 1 ? onboardingTemplates[0].id : undefined,
        }}
        onSubmit={handleFormSubmit}
        validationSchema={InviteWorkersViaCSVSchema}
      >
        <InviteWorkersViaCSVFormContent
          isLoading={isLoading}
          file={file}
          markets={markets}
          onboardingTemplates={onboardingTemplates}
          onRemoveFile={handleRemoveFile}
          onSelectFile={handleUploadFile}
        />
      </Formik>
    </div>
  )
}
